import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  fetchFields,
  fetchGroups,
  fetchJobs,
  patchGuardian,
  patchMember,
  postFieldValue,
  postGroupParticipant,
  postGuardian,
  postJobAssignment,
  postMember,
  postNote,
  postProgramAssignment,
} from "../../services/httpClient";
import DatePicker from "../ui/DatePicker";
import Input from "../ui/Input";
import InputPhone from "../ui/InputPhone";
import SelectObject from "../ui/SelectObject";
import useApp from "../../utils/useApp";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";
import CustomField from "../ui/CustomField";
import CaptureProfilePic from "../ui/CaptureProfilePic";
import { UserFields } from "../../types/userFields";
import { validateUserFields } from "../../helpers/validation";
import FormCheck from "../ui/FormCheckbox";
import { faIdCard, faShieldHalved, faUsersLine } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GroupCard from "./GroupCard";
import IntakeNote from "../ui/IntakeNote";
import StandardNote from "../ui/StandardNote";

interface CreateMemberState {
  possiblePrimaryContacts: any[];
  currentCase?: any;
  relationships: any[];
  genders: any[];
  ethnicities: any[];
  races: any[];
  originalPrograms: any[];
  originalRooms: any[];
  originalBeds: any[];
  myCase: boolean;
  setUserToSave: any;
  getFamily: () => void;
  onClickDoneEdit: (forceNoModal?: boolean) => void;
  setIsSaving: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  // lets a parent render Save and Add Another / Save and Close itself (e.g. as page title actions)
  onSaveStateChange?: (state: { enabled: boolean; save: (isDone: boolean) => void }) => void;
}

const CreateMember = ({
  possiblePrimaryContacts,
  currentCase,
  relationships,
  genders,
  ethnicities,
  races,
  originalPrograms,
  myCase,
  setUserToSave,
  getFamily,
  onClickDoneEdit,
  setIsSaving,
  setShowToast,
  setToastText,
  setToastBody,
  onSaveStateChange,
}: CreateMemberState) => {
  const { id } = useParams();
  const { auth }: any = useApp();
  const [isPrimaryUpdated, setIsPrimaryUpdated] = useState<boolean>(false);
  const [custom, setCustom] = useState<any[]>([]);
  const [type, setType] = useState<string>("0");
  const [note, setNote] = useState<string>("");
  const [noteDate, setNoteDate] = useState<string>("");
  const [profileData, setProfileData] = useState<string>("");
  const [profileImage, setProfileImage] = useState<string>("");
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState("");
  const [programs, setPrograms] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<any>();
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDemo, setShowDemo] = useState(true);
  const [showCustom, setShowCustom] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [userFields, setUserFields] = useState<UserFields>({
    type: "",
    firstName: "",
    lastName: "",
    preferredName: "",
    phone: null,
    intakeDate: undefined,
    exitDate: undefined,
    birthDate: undefined,
    relationship: "",
    gender: "",
    ethnicity: "",
    race: "",
    school: "",
    grade: "",
    program: null,
    level: null,
    job: null,
    room: "",
    bed: null,
    groups: [],
    isPrimary: false,
    isPersonServed: true,
    isResidential: false,
    appAccess: false,
    appAccessLast: undefined,
    isBanned: false,
  });

  var types: any[] = [
    { name: "Primary Adult", key: "primary_adult" },
    { name: "Child", key: "child" },
    { name: "Other", key: "other" },
  ];

  const getPrograms = () => {
    let programs = originalPrograms;
    const updatedPrograms = [{ id: 0, key: 0, name: "Select from List", programLevels: [] }, ...programs];
    setPrograms(updatedPrograms);
  };

  const getJobs = () => {
    fetchJobs().then((res: any) => {
      const jobs = res[0].data;
      var availJobs = jobs.filter((j: any) => j.positions_filled < j.positions_total);
      availJobs = [{ id: 0, key: 0, name: "Select from List" }, ...availJobs];
      setJobs(availJobs);
    });
  };

  const getGroups = () => {
    fetchGroups()
      .then((res: any) => {
        const groupList = res[0].data;
        groupList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setGroups(groupList);
        setAvailableGroups(groupList);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getCustomFields = async () => {
    await fetchFields().then((res: any) => {
      setCustomFields(res[0].data);
    });
  };

  useEffect(() => {
    setIsLoading(true);
    getPrograms();
    getJobs();
    getGroups();
    getCustomFields();
    setNoteDate(new Date().toISOString().split("T")[0]);
    setIsLoading(false);
  }, []);

  function validateUser(
    first: string,
    last: string,
    emailVal: any,
    phoneVal: any,
    level: any,
    levelRequired: boolean,
    appAccess: boolean = false,
    fieldType: string = "user",
  ) {
    let errors: string[] = [];
    errors = validateUserFields(first, last, emailVal, phoneVal, level, levelRequired, appAccess);

    if (setUserToSave) setUserToSave(true);

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  const updateField = (field: keyof typeof userFields, value: UserFields[keyof UserFields], levelRequired = levels.length > 0) => {
    setUserFields((prev) => {
      let updated: UserFields = prev;

      if (prev?.groups && field === "groups") {
        const newGroups = value as UserFields["groups"];
        if (newGroups) {
          updated = { ...prev, groups: [...prev.groups, newGroups] };
        }
      } else {
        updated = { ...prev, [field]: value };
      }

      const validation = validateUser(
        updated.firstName,
        updated.lastName,
        updated.email,
        updated.phone,
        updated.level,
        levelRequired,
        updated.appAccess,
      );
      setSaveIsEnabled(validation.isValid);
      if (setUserToSave) setUserToSave(true);
      return updated;
    });
  };

  const updateType = (e: any) => {
    setType(e.target.value);
    updateField("appAccess", false);
    if (e.target.value === "child") {
      updateField("relationship", "child");
    } else {
      updateField("relationship", "");
    }
  };

  const updateProgram = (e: any) => {
    updateField("level", null);
    let updatedLevels = [];
    setLevels([]);
    if (e.programLevels && e.programLevels.length > 0) {
      updatedLevels = e.programLevels;
    }
    setLevels(updatedLevels);
    updateField("program", e, updatedLevels.length > 0);
  };

  const updateLevel = (e: any) => {
    if (e.key !== userFields.level?.key) {
      updateField("level", e);
    }
  };

  const updateJob = (e: any) => {
    if (e.key !== userFields.job?.key) {
      updateField("job", e);
    }
  };

  const updateResidential = (e: any) => {
    updateField("isResidential", !userFields.isResidential);
  };

  const updateGroup = (e: any) => {
    setSelectedGroup(e);
  };

  const updateIsPrimary = () => {
    if (!userFields.isPrimary) {
      setIsPrimaryUpdated(true);
    } else {
      setIsPrimaryUpdated(false);
    }
    updateField("isPrimary", !userFields.isPrimary);
  };

  async function uploadPhoto(userId: string, data: any) {
    const image = {
      profile_data: data,
    };

    if (type === "primary_adult") {
      patchGuardian(image, id, userId).then((res: any) => {
        setProfileData("");
        setProfileImage("");
      });
    } else {
      patchMember(image, id, userId).then((res: any) => {
        setProfileData("");
        setProfileImage("");
      });
    }
  }

  const saveMember = (isDone: boolean) => {
    const validation = validateUser(
      userFields.firstName,
      userFields.lastName,
      userFields.email,
      userFields.phone,
      userFields.level,
      levels.length > 0,
      userFields.appAccess,
    );
    setErrMsg("");

    if (validation.isValid) {
      setIsSaving(true);

      if (type === "primary_adult") {
        if (isPrimaryUpdated) {
          const otherUpdatedGuardian = {
            is_primary: false,
          };

          possiblePrimaryContacts.map((o: any) => {
            patchGuardian(otherUpdatedGuardian, id, o?.id)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          });
        }

        let newGuardian: any = {
          firstname: userFields.firstName,
          lastname: userFields.lastName,
          preferredname: userFields.preferredName,
          email: userFields.email,
          phonenumber: userFields.phone === "1" ? null : userFields.phone,
          relationship: userFields.relationship,
          intake_at: userFields.intakeDate,
          birthdate: userFields.birthDate,
          gender: userFields.gender,
          race: userFields.race,
          ethnicity: userFields.ethnicity,
          is_primary: userFields.isPrimary,
          is_case_subject: userFields.isPersonServed,
          appaccess: userFields.appAccess,
        };

        if (auth?.profile?.includeHMIS === 1) {
          newGuardian = { ...newGuardian, is_residential: userFields.isResidential };
        }

        //create new guardian
        postGuardian(newGuardian, id)
          .then((res: any) => {
            const userId = res[0].data.user_id;
            // setActiveMembers((prev: any) => [...prev, newGuardian]);
            setShowToast(true);
            setToastText("Primary Adult Created");
            const toast = userFields.preferredName
              ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
              : userFields.firstName + " " + userFields.lastName;
            setToastBody(toast);

            //update / create custom field values
            custom.map((cust: any) => {
              //post
              const fieldVal = {
                field_id: cust?.field_id,
                person_id: userId,
                person_type: "guardian",
                value: cust?.value,
              };

              postFieldValue(fieldVal).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            });

            if (currentCase?.casestatus === "active") {
              const intakeNote = {
                notetype: "intake",
                subject: {
                  id: userId,
                  type: "guardian",
                },
                fields: {
                  description: note,
                  date: noteDate,
                },
              };

              //save intake note
              postNote(intakeNote, id)
                .then((res: any) => {
                  const noteId = res[0].data.note_id;

                  //update user intake data
                  const intakeData = {
                    intake_at: noteDate,
                    intake_note_id: noteId,
                  };

                  patchGuardian(intakeData, id, userId).catch((err: any) => {
                    console.log(err);
                  });
                })
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg || "Failed to save note");
                });
            } else if (note) {
              const newNote = {
                notetype: "standard",
                subject: {
                  id: userId,
                  type: "guardian",
                },
                fields: {
                  text: note,
                  date: noteDate,
                },
              };

              postNote(newNote, id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg || "Failed to save note");
                });
            }

            if (userFields.job) {
              const newJob = {
                job_id: userFields.job?.id,
                user_id: userId,
              };

              postJobAssignment(newJob, id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }

            if (userFields.program) {
              const newProgram = {
                program_id: userFields.program?.id,
                level_id: userFields.level?.id,
                user_id: userId,
              };

              postProgramAssignment(newProgram, id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }

            if (userFields.groups && userFields.groups.length > 0) {
              const newParticipant = {
                participant_id: userId,
                participant_type: "guardian",
              };

              userFields.groups.map((g: any) => {
                postGroupParticipant(newParticipant, g?.id)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              });
            }

            if (profileData) {
              uploadPhoto(userId, profileData);
            }
          })
          .catch((err: any) => {
            setErrMsg(err?.response?.data?.msg);
            setIsSaving(false);
          })
          .finally(() => {
            setSaveIsEnabled(false);
            setUserToSave(false);
            setShowToast(true);
            setToastText("Primary Adult Created");
            const toast = userFields.preferredName
              ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
              : userFields.firstName + " " + userFields.lastName;
            setToastBody(toast);
            clearMember();

            setTimeout(() => {
              if (isDone) {
                onClickDoneEdit(true);
              } else {
                getFamily();
                getJobs();
              }

              setIsSaving(false);
            }, 3000);
          });
      } else if (type === "other" || type === "child") {
        let newMember: any = {
          firstname: userFields.firstName,
          lastname: userFields.lastName,
          preferredname: userFields.preferredName,
          phonenumber: userFields.phone === "1" ? null : userFields.phone,
          intake_at: userFields.intakeDate,
          exit_at: userFields.exitDate,
          birthdate: userFields.birthDate,
          relationship: userFields.relationship,
          gender: userFields.gender,
          ethnicity: userFields.ethnicity,
          race: userFields.race,
          is_case_subject: userFields.isPersonServed,
        };

        if (type === "child") {
          newMember = { ...newMember, school: userFields.school, grade: userFields.grade };
        }

        if (auth?.profile?.includeHMIS === 1) {
          newMember = { ...newMember, is_residential: userFields.isResidential };
        }

        //create new member
        postMember(newMember, id)
          .then((res: any) => {
            const memberId = res[0].data.member_id;
            setShowToast(true);
            setToastText("Member Created");
            const toast = userFields.preferredName
              ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
              : userFields.firstName + " " + userFields.lastName;
            setToastBody(toast);

            //update / create custom field values
            custom.map((cust: any) => {
              //post
              const fieldVal = {
                field_id: cust?.field_id,
                person_id: memberId,
                person_type: "member",
                value: cust?.value,
              };

              postFieldValue(fieldVal).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            });

            if (currentCase?.casestatus === "active") {
              const intakeNote = {
                notetype: "intake",
                subject: {
                  id: memberId,
                  type: "member",
                },
                fields: {
                  description: note,
                  date: noteDate,
                },
              };

              //save intake note
              postNote(intakeNote, id)
                .then((res: any) => {
                  const noteId = res[0].data.note_id;

                  //update user intake data
                  const intakeData = {
                    intake_at: noteDate,
                    intake_note_id: noteId,
                  };

                  patchMember(intakeData, id, memberId).catch((err: any) => {
                    console.log(err);
                  });
                })
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg || "Failed to save note");
                });
            } else if (note) {
              const newNote = {
                notetype: "standard",
                subject: {
                  id: memberId,
                  type: "member",
                },
                fields: {
                  text: note,
                  date: noteDate,
                },
              };

              postNote(newNote, id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg || "Failed to save note");
                });
            }

            if (userFields.job) {
              const newJob = {
                job_id: userFields.job?.id,
                member_id: memberId,
              };

              postJobAssignment(newJob, id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }

            if (userFields.program) {
              const newProgram = {
                program_id: userFields.program?.id,
                level_id: userFields.level?.id,
                member_id: memberId,
              };

              postProgramAssignment(newProgram, id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }

            if (userFields.groups && userFields.groups.length > 0) {
              const newParticipant = {
                participant_id: memberId,
                participant_type: "member",
              };

              userFields.groups.map((g: any) => {
                postGroupParticipant(newParticipant, g?.id)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              });
            }

            if (profileData) {
              uploadPhoto(memberId, profileData);
            }
          })
          .catch((err: any) => {
            setErrMsg(err?.response?.data?.msg);
            setIsSaving(false);
          })
          .finally(() => {
            setSaveIsEnabled(false);
            setUserToSave(false);
            setShowToast(true);
            setToastText("Member Created");
            const toast = userFields.preferredName
              ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
              : userFields.firstName + " " + userFields.lastName;
            setToastBody(toast);
            clearMember();

            setTimeout(() => {
              if (isDone) {
                onClickDoneEdit(true);
              } else {
                getFamily();
                getJobs();
              }

              setIsSaving(false);
            }, 3000);
          });
      }
    } else {
      validation.errors.map((err: string) => {
        setErrMsg((prev) => (prev ? prev + "  |  " + err : err));
        return true;
      });
    }
  };

  const onClickAddGroup = () => {
    let userGroupIds = new Set(userFields?.groups?.map((g: any) => g.id));
    userGroupIds.add(selectedGroup?.id);
    const available = groups.filter((g: any) => !userGroupIds.has(g.id));
    setAvailableGroups(available);
    setSelectedGroup(null);
    updateField("groups", selectedGroup);
  };

  const updateSelection = (type: string) => {
    switch (type) {
      case "demo":
        setShowDemo(true);
        setShowCustom(false);
        setShowAssign(false);
        break;
      case "custom":
        setShowDemo(false);
        setShowCustom(true);
        setShowAssign(false);
        break;
      case "assign":
        setShowDemo(false);
        setShowCustom(false);
        setShowAssign(true);
        break;
      default:
        setShowDemo(true);
        setShowCustom(false);
        setShowAssign(false);
        break;
    }
  };

  const clearMember = () => {
    setType("");
    setUserFields({
      firstName: "",
      lastName: "",
      preferredName: "",
      email: "",
      phone: null,
      intakeDate: undefined,
      exitDate: undefined,
      birthDate: undefined,
      relationship: "",
      gender: "",
      ethnicity: "",
      race: "",
      school: "",
      grade: "",
      program: null,
      level: null,
      job: null,
      room: "",
      bed: null,
      isPrimary: false,
      isPersonServed: true,
      isResidential: false,
      appAccess: false,
      appAccessLast: undefined,
      isBanned: false,
    });
    setNote("");
    setNoteDate(new Date().toISOString().split("T")[0]);
    setCustom([]);
  };

  // saveMember is recreated every render; the ref keeps the callback handed to the parent stable
  const saveMemberRef = useRef(saveMember);
  saveMemberRef.current = saveMember;
  const saveMemberStable = useCallback((isDone: boolean) => saveMemberRef.current(isDone), []);

  useEffect(() => {
    onSaveStateChange?.({ enabled: saveIsEnabled, save: saveMemberStable });
  }, [saveIsEnabled]);

  return (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <div>
          <Stack gap={2} className="mb-3">
            <Row>
              <Col xs={5} xl={4}>
                <SelectObject label="Household Member Type" value={type} options={types} onUpdate={(e: any) => updateType(e)} />
              </Col>
              {!onSaveStateChange && (
                <Col xs={7} xl={8}>
                  <Stack className="stack-float" direction="horizontal" gap={3}>
                    <Button className="app-secondary-color button" onClick={() => saveMember(false)} disabled={!saveIsEnabled}>
                      Save and Add Another
                    </Button>
                    <div className="vr" />
                    <Button className="app-secondary-color button" onClick={() => saveMember(true)} disabled={!saveIsEnabled}>
                      Save and Close
                    </Button>
                  </Stack>
                </Col>
              )}
            </Row>
            <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
              {errMsg}
            </p>
            {type !== "0" && (
              <>
                <Row className="my-3">
                  <Col xs={4} className="d-grid">
                    <Button
                      className={showDemo ? "app-primary-color button button-toggle" : "app-primary-color-inverse button button-toggle"}
                      onClick={() => updateSelection("demo")}
                    >
                      <FontAwesomeIcon icon={faIdCard} className={"me-2"} />
                      {"Demographics"}
                    </Button>
                  </Col>
                  <Col xs={4} className="d-grid">
                    <Button
                      className={showCustom ? "app-primary-color button button-toggle" : "app-primary-color-inverse button button-toggle"}
                      onClick={(e) => updateSelection("custom")}
                    >
                      <FontAwesomeIcon icon={faShieldHalved} className={"me-2"} />
                      {"Custom Fields"}
                    </Button>
                  </Col>
                  <Col xs={4} className="d-grid">
                    <Button
                      className={showAssign ? "app-primary-color button button-toggle" : "app-primary-color-inverse button button-toggle"}
                      onClick={(e) => updateSelection("assign")}
                    >
                      <FontAwesomeIcon icon={faUsersLine} className={"me-2"} />
                      {"Assignments"}
                    </Button>
                  </Col>
                </Row>

                {showDemo && (
                  <>
                    <Row className="h-auto">
                      <Col xs={12} md={6} xl={4}>
                        <Input
                          label="First Name"
                          type="text"
                          value={userFields.firstName}
                          onUpdate={(e: any) => updateField("firstName", e.target.value)}
                          required={true}
                          css={"mb-3"}
                        />
                      </Col>
                      <Col xs={12} md={6} xl={4}>
                        <Input
                          label="Preferred Name"
                          type="text"
                          value={userFields.preferredName}
                          onUpdate={(e: any) => updateField("preferredName", e.target.value)}
                          css={"mb-3"}
                        />
                      </Col>
                      <Col xs={12} md={6} xl={4}>
                        <Input
                          label="Last Name"
                          type="text"
                          value={userFields.lastName}
                          onUpdate={(e: any) => updateField("lastName", e.target.value)}
                          required={true}
                          css={"mb-3"}
                        />
                      </Col>
                      {type === "primary_adult" && (
                        <Col xs={12} md={6} xl={4}>
                          <Input
                            label="Email"
                            type="text"
                            value={userFields.email}
                            onUpdate={(e: any) => updateField("email", e.target.value)}
                            required={userFields.appAccess}
                            css={"mb-3"}
                          />
                        </Col>
                      )}
                      <Col xs={12} md={6} xl={4}>
                        <InputPhone label="Phone" value={userFields.phone} onUpdate={(e: any) => updateField("phone", e)} css={"mb-3"} />
                      </Col>
                      <Col xs={12} md={6} xl={4}>
                        <DatePicker
                          label="Birth Date"
                          currentValue={userFields.birthDate}
                          setCurrentValue={(e: any) => updateField("birthDate", e)}
                          hasMaxDate={true}
                          disabled={myCase ? false : true}
                          css={"mb-3"}
                        />
                      </Col>
                      <Col xs={12} md={6} xl={4}>
                        <SelectObject
                          label="Gender"
                          value={userFields.gender}
                          options={genders}
                          onUpdate={(e: any) => updateField("gender", e.target.value)}
                          disabled={myCase ? false : true}
                          cssClasses={"mb-3"}
                        />
                      </Col>
                      <Col xs={12} md={6} xl={4}>
                        <SelectObject
                          label="Ethnicity"
                          value={userFields.ethnicity}
                          options={ethnicities}
                          onUpdate={(e: any) => updateField("ethnicity", e.target.value)}
                          disabled={myCase ? false : true}
                          cssClasses={"mb-3"}
                        />
                      </Col>
                      <Col xs={12} md={6} xl={4}>
                        <SelectObject
                          label="Race"
                          value={userFields.race}
                          options={races}
                          onUpdate={(e: any) => updateField("race", e.target.value)}
                          disabled={myCase ? false : true}
                          cssClasses={"mb-3"}
                        />
                      </Col>
                      {userFields.relationship === "child" && (
                        <>
                          <Col xs={12} md={6} xl={4}>
                            <Input
                              label="School"
                              type="text"
                              value={userFields.school}
                              onUpdate={(e: any) => updateField("school", e.target.value)}
                              disabled={myCase ? false : true}
                              css={"mb-3"}
                            />
                          </Col>
                          <Col xs={12} md={6} xl={4}>
                            <Input
                              label="Grade"
                              type="text"
                              value={userFields.grade}
                              onUpdate={(e: any) => updateField("grade", e.target.value)}
                              disabled={myCase ? false : true}
                              css={"mb-3"}
                            />
                          </Col>
                        </>
                      )}
                      <Col xs={12} md={6} xl={4}>
                        <SelectObject
                          label="Relationship"
                          value={userFields.relationship}
                          options={relationships}
                          onUpdate={(e: any) => updateField("relationship", e.target.value)}
                          disabled={myCase ? false : true}
                          cssClasses={"mb-3"}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={8}>
                        <CaptureProfilePic
                          userName={userFields.firstName + " " + userFields.lastName}
                          userType={type === "primary_adult" ? "guardian" : "member"}
                          myCase={myCase}
                          profileImage={profileImage}
                          setProfileImage={setProfileImage}
                          setProfileData={setProfileData}
                          setErrMsg={setErrMsg}
                          setShowToast={setShowToast}
                          setToastText={setToastText}
                          setToastBody={setToastBody}
                        />
                      </Col>
                      <Col xs={4}>
                        <Stack gap={2}>
                          <FormCheck
                            label="Person Served"
                            value={userFields.isPersonServed}
                            onUpdate={(e: any) => updateField("isPersonServed", !userFields.isPersonServed)}
                          />
                          {auth?.profile.includeHMIS === 1 && (
                            <FormCheck label="Residential" value={userFields.isResidential} onUpdate={(e: any) => updateResidential(e)} />
                          )}
                          {type === "primary_adult" && (
                            <>
                              <FormCheck label="Main Contact" value={userFields.isPrimary} onUpdate={(e: any) => updateIsPrimary()} />
                              <div>
                                <FormCheck
                                  label="Mobile App Access"
                                  value={userFields.appAccess}
                                  onUpdate={(e: any) => updateField("appAccess", !userFields.appAccess)}
                                  css={"mb-0"}
                                />
                                <small>{"(Require email or phone)"}</small>
                              </div>
                            </>
                          )}
                        </Stack>
                      </Col>
                    </Row>
                    <h4 className="app-header-text mt-3">Note</h4>
                    <Row className="mb-3">
                      {currentCase?.casestatus === "active" ? (
                        <IntakeNote intakeNote={note} setIntakeNote={setNote} intakeDate={noteDate} setIntakeDate={setNoteDate} />
                      ) : (
                        <StandardNote note={note} setNote={setNote} noteDate={noteDate} setNoteDate={setNoteDate} />
                      )}
                    </Row>
                  </>
                )}
                {showCustom && customFields?.length > 0 && (
                  <>
                    <Row className="h-auto">
                      {customFields?.map((c: any) => (
                        <CustomField
                          customField={c}
                          setCustom={setCustom}
                          enableSave={() =>
                            validateUser(
                              userFields.firstName,
                              userFields.lastName,
                              userFields.email,
                              userFields.phone,
                              userFields.level,
                              levels.length > 0,
                              false,
                              "custom",
                            )
                          }
                          setSaveIsEnabled={setSaveIsEnabled}
                          myCase={myCase}
                          editMode={true}
                        />
                      ))}
                    </Row>
                  </>
                )}
                {showAssign && (
                  <>
                    <Row>
                      {programs.length > 0 && (
                        <Row>
                          {programs.length > 0 && (
                            <Col xs={12} lg={6}>
                              <SelectObjectTypeahead
                                label="Program"
                                value={userFields.program?.key}
                                options={programs}
                                onUpdate={(e: any) => updateProgram(e)}
                                disabled={userFields.isBanned ? true : myCase ? false : true}
                                cssClasses={"mb-3"}
                              />
                            </Col>
                          )}
                          {levels.length > 0 && (
                            <Col xs={12} lg={6}>
                              <SelectObjectTypeahead
                                label="Program Level"
                                value={userFields.level?.key}
                                options={levels}
                                onUpdate={(e: any) => updateLevel(e)}
                                disabled={userFields.isBanned ? true : myCase ? false : true}
                                cssClasses={"mb-3"}
                              />
                            </Col>
                          )}
                        </Row>
                      )}
                      {jobs.length > 0 && (
                        <Row>
                          <Col xs={12} lg={6}>
                            <SelectObjectTypeahead
                              label="Job"
                              value={userFields.job?.key}
                              options={jobs}
                              onUpdate={(e: any) => updateJob(e)}
                              disabled={userFields.isBanned ? true : myCase ? false : true}
                              cssClasses={"mb-3"}
                            />
                          </Col>
                        </Row>
                      )}
                    </Row>
                    <h5 className="app-header-text mb-0">Groups</h5>
                    <Row className="align-items-start">
                      {userFields?.groups && userFields?.groups?.length > 0 && (
                        <Col xs={12} className="mb-1">
                          <Stack>
                            {userFields?.groups.map((g: any) => (
                              <GroupCard
                                key={g?.id}
                                group={g}
                                editMode={false}
                                setUserFields={setUserFields}
                                groups={groups}
                                setAvailableGroups={setAvailableGroups}
                                setShowToast={setShowToast}
                                setToastText={setToastText}
                                setToastBody={setToastBody}
                              />
                            ))}
                          </Stack>
                        </Col>
                      )}
                      <Col xs={12}>
                        <Stack gap={2}>
                          <SelectObjectTypeahead
                            key={availableGroups?.length ?? 0}
                            label="Groups"
                            value={selectedGroup?.key}
                            options={availableGroups}
                            onUpdate={(e: any) => updateGroup(e)}
                          />
                          <Col>
                            <Button className="app-tertiary-color button" disabled={selectedGroup ? false : true} onClick={onClickAddGroup}>
                              Add
                            </Button>
                          </Col>
                        </Stack>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )}
          </Stack>
        </div>
      )}
    </>
  );
};

export default CreateMember;
